import { z } from 'zod'

export const channelBroadcastTypeSchema = z.enum(['announcement', 'update', 'event', 'drop'])

export type ChannelBroadcastType = z.infer<typeof channelBroadcastTypeSchema>

export const channelBroadcastRequestSchema = z.object({
  message: z.string().min(1).max(2000),
  type: channelBroadcastTypeSchema.default('announcement'),
  eventId: z.string().uuid().optional(),
  dropId: z.string().uuid().optional(),
  city: z.string().min(1).max(100).optional(),
})

/** Payload of a `drop_share` chat message; deep-links to the drop page. */
export const dropShareMetadataSchema = z.object({
  dropId: z.string().uuid(),
  title: z.string(),
  prizeName: z.string().nullable(),
  heroImageUrl: z.string().nullable(),
})

export type DropShareMetadata = z.infer<typeof dropShareMetadataSchema>

export type ChannelBroadcastRequest = z.infer<typeof channelBroadcastRequestSchema>

export const channelBroadcastDtoSchema = z.object({
  id: z.string().uuid(),
  message: z.string(),
  type: channelBroadcastTypeSchema,
  eventId: z.string().uuid().nullable(),
  eventTitle: z.string().nullable(),
  dropId: z.string().uuid().nullable(),
  dropTitle: z.string().nullable(),
  city: z.string().nullable(),
  recipientCount: z.number().int(),
  sentCount: z.number().int(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
})

export type ChannelBroadcastDto = z.infer<typeof channelBroadcastDtoSchema>

export const channelBroadcastListResponseSchema = z.object({
  broadcasts: z.array(channelBroadcastDtoSchema),
})

export type ChannelBroadcastListResponse = z.infer<typeof channelBroadcastListResponseSchema>

export const channelInboxConversationSchema = z.object({
  conversationId: z.string().uuid(),
  user: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
  }),
  lastMessageBody: z.string().nullable(),
  lastMessageKind: z.string(),
  lastMessageFromUser: z.boolean(),
  lastMessageAt: z.string(),
  unreadCount: z.number().int(),
})

export type ChannelInboxConversation = z.infer<typeof channelInboxConversationSchema>

export const channelInboxResponseSchema = z.object({
  conversations: z.array(channelInboxConversationSchema),
})

export type ChannelInboxResponse = z.infer<typeof channelInboxResponseSchema>

export const channelThreadMessageSchema = z.object({
  id: z.string().uuid(),
  fromSystem: z.boolean(),
  kind: z.string(),
  body: z.string().nullable(),
  createdAt: z.string(),
})

export type ChannelThreadMessage = z.infer<typeof channelThreadMessageSchema>

export const channelThreadResponseSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    avatarUrl: z.string().nullable(),
  }),
  messages: z.array(channelThreadMessageSchema),
})

export type ChannelThreadResponse = z.infer<typeof channelThreadResponseSchema>

export const channelReplyRequestSchema = z.object({
  message: z.string().min(1).max(2000),
})

export type ChannelReplyRequest = z.infer<typeof channelReplyRequestSchema>

export const channelAudienceQuerySchema = z.object({
  city: z.string().min(1).max(100).optional(),
})

export const channelAudienceResponseSchema = z.object({
  count: z.number().int(),
})

export type ChannelAudienceResponse = z.infer<typeof channelAudienceResponseSchema>

export const channelModeValues = ['announcements', 'open'] as const

/** Posting mode for Pulsify-channel conversations; `announcements` is the default. */
export const channelModeSchema = z.object({
  mode: z.enum(channelModeValues),
})

export type ChannelMode = z.infer<typeof channelModeSchema>

/** Result of switching the channel mode: the new mode plus how many existing
 * system conversations had their read-only flag bulk-toggled. */
export const channelModeResponseSchema = z.object({
  mode: z.enum(channelModeValues),
  updatedConversations: z.number().int(),
})

export type ChannelModeResponse = z.infer<typeof channelModeResponseSchema>
